import * as React from 'react'
import { ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'

export interface InvoiceSelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface InvoiceMultiSelectProps {
  options: InvoiceSelectOption[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  label?: string | React.ReactNode
  error?: boolean
  errorMessage?: string
}

export const InvoiceMultiSelect = React.forwardRef<
  HTMLButtonElement,
  InvoiceMultiSelectProps
>(
  (
    {
      options,
      value = [],
      onValueChange,
      placeholder = 'Select invoices...',
      searchPlaceholder = 'Search invoices...',
      emptyMessage = 'No invoices found.',
      disabled = false,
      className,
      label,
      error = false,
      errorMessage,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    // Create a stable map of label to value for reliable selection
    const labelToValueMap = React.useMemo(() => {
      const map = new Map<string, string>()
      options.forEach((option) => {
        if (option && option.label && typeof option.label === 'string') {
          map.set(option.label, option.value)
        }
      })
      return map
    }, [options])

    const selectedCount = value.length

    const handleSelect = (selectedValue: string) => {
      if (!onValueChange) return // Prevent changes if onValueChange is not provided
      const currentValues = value || []
      if (currentValues.includes(selectedValue)) {
        // Remove if already selected
        onValueChange(currentValues.filter(v => v !== selectedValue))
      } else {
        // Add if not selected
        onValueChange([...currentValues, selectedValue])
      }
      // Don't close popover on multiselect
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onValueChange) {
        onValueChange([])
      }
    }

    return (
      <div className="space-y-2">
        {label && (
          <label className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', error && 'text-destructive')}>
            {label}
          </label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between min-h-9 h-auto',
                selectedCount === 0 && 'text-muted-foreground',
                error && 'border-destructive',
                className
              )}
            >
              <div className="flex flex-1 text-left">
                {selectedCount === 0 ? (
                  <span className="text-muted-foreground">{placeholder}</span>
                ) : (
                  <span>
                    {selectedCount} {selectedCount === 1 ? 'invoice' : 'invoices'} selected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                {value && value.length > 0 && !disabled && onValueChange && (
                  <X
                    className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 cursor-pointer"
                    onClick={handleClear}
                  />
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[250px] p-0" align="start">
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {options.filter(opt => opt && opt.label && opt.value).map((option) => {
                    const isSelected = value.includes(option.value)
                    return (
                      <CommandItem
                        key={`${option.value}-${option.label}`}
                        value={option.label || ''}
                        disabled={option.disabled}
                        onSelect={(selectedLabel) => {
                          if (!selectedLabel || typeof selectedLabel !== 'string') {
                            return
                          }
                          
                          const actualValue = labelToValueMap.get(selectedLabel) || 
                            Array.from(labelToValueMap.entries()).find(
                              ([label]) => label && typeof label === 'string' && label.toLowerCase() === selectedLabel.toLowerCase()
                            )?.[1]
                          
                          if (actualValue) {
                            handleSelect(actualValue)
                          } else {
                            const matchedOption = options.find(opt => opt && opt.label === selectedLabel)
                            if (matchedOption) {
                              handleSelect(matchedOption.value)
                            }
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSelect(option.value)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={option.disabled || !onValueChange}
                            className="mr-2"
                          />
                          <span className="flex-1">{option.label}</span>
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && errorMessage && (
          <p className="text-sm text-destructive mt-1">{errorMessage}</p>
        )}
      </div>
    )
  }
)

InvoiceMultiSelect.displayName = 'InvoiceMultiSelect'
